import React from 'react'; // enable JSX
import PropTypes from 'prop-types';
import { get, template, flowRight as compose } from 'lodash';
import snarkdown from 'snarkdown';

// Get the translations from the backend through the config, or use a fallback
const translations = get(globals, 'config.translations') || {};
const PLURALIZATION_REGEX = /\|\|\|\|/m;


/**
 * Normalize a translation string in order to wrap its content in <p>
 * @param {string} text
 * @return {string}
 */
export function normalizeToHTML(text) {
    return text
        .replace(/^/, `<p>`)
        .replace(/(\r\n){2}/gm, '<p>')
        .replace(/\r\n/gm, '<br/>');
}

/**
 * Add target blank to links starting with https://
 * @param {string} text
 * @return {string}
 */
export function externalLinks(text) {
    return text.replace(
        /(<a href="(https?:)?\/\/.*?")>/g,
        '$1 target="_blank" rel="nofollow">',
    );
}


/**
 * Pluralize the sentences if the `||||` separator get detected 4 example: "My singular |||| My plural |||| My super plural"
 * @param {string} translation - translation string
 * @param {number} amount - amount to manage the pluralization
 * @returns {string}
 */
function pluralize(translation, amount) {
    const isPluralized = PLURALIZATION_REGEX.exec(translation);

    if (isPluralized) {
        // take all the variations for the same vocabulary string
        const variants = translation.split(PLURALIZATION_REGEX);
        // fallback solution for values having not a specific value
        const lastVariation = variants[variants.length - 1];

        return amount > 1 ? variants[amount - 1] || lastVariation : variants[0];
    }

    return translation;
}

/**
 * Print meaningful information for the user doing translations
 * @param {string} path - path to the translations
 * @param {object} data - optional object
 * @returns {string}
 */
function printInfo(path, data) {
    if (!data) {
        return path;
    }

    return `path="${ path }" and data="${ JSON.stringify(data) }"`;
}

/**
 * Get the site static content translations
 * @param {string} path - key to the content
 * @param {object} data - data to interpolate into the translation string
 * @param {number} amount - variable used to pluralize our texts
 * @returns {string}
 */
export function t(path, data, amount) {
    const translation = get(translations, path, path);

    const words = typeof amount === 'number' ? pluralize(translation, amount) : translation;

    // use the lodash template engine in case we want to use interpolate translations variables
    if (data) {
        return template(words)(data);
    }

    return words;
}

export const markdown = compose(externalLinks, snarkdown, t);
export const format = compose(normalizeToHTML, markdown);

/**
 * Get the site static content translations and wrap it for the dom
 * The title param gives us the info if the user is a staff member
 * @param {string} path - path to the content
 * @param {object} data
 * @param {number} amount
 * @param {any} props
 * @returns {string}
 */
export function Trans({ path, data, amount, ...props }) {
    return (
        <span {...props} title={globals.config.user.isStaff ? printInfo(path, data) : null}>
            {t(path, data, amount)}
        </span>
    );
}

Trans.propTypes = {
    path: PropTypes.string.isRequired,
    data: PropTypes.object,
    amount: PropTypes.number,
};

/**
 * Like trans but with markdown support
 * @param {string} path - path to the content
 * @returns {string}
 */
export function TransMD({ path, data, amount, ...props }) {
    return (
        <span
            {...props}
            title={globals.config.user.isStaff ? printInfo(path, data) : null}
            dangerouslySetInnerHTML={{ __html: format(path, data, amount) }}>
        </span>
    );
}

TransMD.propTypes = {
    path: PropTypes.string.isRequired,
    data: PropTypes.object,
    amount: PropTypes.number,
};

export default t;



// WEBPACK FOOTER //
// ./mcart/assets/src/js/app/translations/index.js